import React, { useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment/moment";

import dashboardStatIconWhite from "../../../assets/img/icons/dashboard-stat-icon-white.svg";
import {
  AttendanceCircle,
  AttendanceLegendDot,
  AttendanceLegendItem,
  AttendanceLegendText,
  TodaysAttendanceChartContainer,
  TodaysAttendanceContainer,
  TodaysAttendanceHeader,
  TodaysAttendanceIconButton,
  TodaysAttendanceTitle,
  TodaysAttendanceSkeletonTitle,
  TodaysAttendanceSkeletonIcon,
  TodaysAttendanceSkeletonCircle,
  TodaysAttendanceSkeletonLegend,
} from "../../DashBoardNew/dashBoardNewStyles";

const calculateBubbleSize = (value, total) => {
  const MAX_BUBBLE_SIZE = 120;
  const MIN_BUBBLE_SIZE = 48;

  if (total === 0) return MIN_BUBBLE_SIZE;

  const ratio = value / total;
  const calculatedSize = ratio * MAX_BUBBLE_SIZE;

  return Math.max(MIN_BUBBLE_SIZE, Math.min(calculatedSize, MAX_BUBBLE_SIZE));
};

/**
 * Styled component for centered text overlay
 */
const BubbleText = styled.div`
  position: absolute;
  font-family: "Mona Sans", sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
  color: #000e2a;
  text-align: center;
  z-index: ${({ zIndex }) => zIndex};
  pointer-events: none;

  /* Perfect centering using flex */
  display: flex;
  align-items: center;
  justify-content: center;
`;



const TodaysAttendance = ({
  attendanceData,
  getDashboardAttendance,
  attendanceIsLoading,
  selectedOrganization,
  history,
}) => {
  const [activeBubble, setActiveBubble] = useState(null);

  // Fetch attendance data on component mount or when organization changes
  useEffect(() => {
    if (selectedOrganization?.id && getDashboardAttendance) {
      const startTime = moment().format("YYYY-MM-DD");
      const endTime = moment().format("YYYY-MM-DD");

      const payload = {
        organization_id: selectedOrganization.id,
        start_time: startTime,
        end_time: endTime,
      };
      getDashboardAttendance(payload);
    }
  }, [selectedOrganization, getDashboardAttendance]);

  // Provide safe default values if attendanceData is null
  const safeAttendanceData = attendanceData || {
    total_members: 0,
    present: 0,
    late: 0,
    absent: 0,
  };

  // Calculate dynamic bubble sizes
  const totalSize = calculateBubbleSize(
    safeAttendanceData.total_members,
    safeAttendanceData.total_members,
  );
  const presentSize = calculateBubbleSize(
    safeAttendanceData.present,
    safeAttendanceData.total_members,
  );
  const lateSize = calculateBubbleSize(
    safeAttendanceData.late,
    safeAttendanceData.total_members,
  );
  const absentSize = calculateBubbleSize(
    safeAttendanceData.absent,
    safeAttendanceData.total_members,
  );

  // Base positions from Figma with legend offsets (legends move with circles)
  // Legend offset is calculated as: legendX - circleX, legendY - circleY
  const circles = [
    {
      label: "Total",
      value: safeAttendanceData.total_members,
      color: "#cbd5e1",
      size: totalSize,
      x: 60,
      y: 70,
      zIndex: 1,
      legendOffsetX: -60,
      legendOffsetY: -34,
    },
    {
      label: "Present",
      value: safeAttendanceData.present,
      color: "#8db1f7",
      size: presentSize,
      x: 15.33,
      y: 103,
      zIndex: 2,
      legendOffsetX: -20.33,
      legendOffsetY: 10,
    },
    {
      label: "Late",
      value: safeAttendanceData.late,
      color: "#ffd699",
      size: lateSize,
      x: 107.83,
      y: 32,
      zIndex: 3,
      legendOffsetX: 2.17,
      legendOffsetY: -31,
    },
    {
      label: "Absent",
      value: safeAttendanceData.absent,
      color: "#fda4af",
      size: absentSize,
      x: 103.16,
      y: 125,
      zIndex: 4,
      legendOffsetX: 30,
      legendOffsetY: 12,
    },
  ];

  // Render skeleton loading state
  if (attendanceIsLoading) {
    return (
      <TodaysAttendanceContainer>
        <TodaysAttendanceHeader>
          <TodaysAttendanceSkeletonTitle />
          <TodaysAttendanceSkeletonIcon />
        </TodaysAttendanceHeader>

        <TodaysAttendanceChartContainer>
          {/* Skeleton circles at approximate positions */}
          <TodaysAttendanceSkeletonCircle
            style={{
              width: "96px",
              height: "96px",
              left: "50px",
              top: "50px",
              transform: "translate(-50%, -50%)",
            }}
          />
          <TodaysAttendanceSkeletonCircle
            style={{
              width: "72px",
              height: "72px",
              left: "30px",
              top: "93px",
              transform: "translate(-50%, -50%)",
            }}
          />
          <TodaysAttendanceSkeletonCircle
            style={{
              width: "64px",
              height: "64px",
              left: "107.83px",
              top: "32px",
              transform: "translate(-50%, -50%)",
            }}
          />
          <TodaysAttendanceSkeletonCircle
            style={{
              width: "56px",
              height: "56px",
              left: "103.16px",
              top: "125px",
              transform: "translate(-50%, -50%)",
            }}
          />

          {/* Skeleton legends at approximate positions */}
          <TodaysAttendanceSkeletonLegend
            style={{
              left: "0px",
              top: "36px",
            }}
          />
          <TodaysAttendanceSkeletonLegend
            style={{
              left: "110px",
              top: "1px",
            }}
          />
          <TodaysAttendanceSkeletonLegend
            style={{
              left: "133px",
              top: "107px",
            }}
          />
          <TodaysAttendanceSkeletonLegend
            style={{
              left: "87.33px",
              top: "150px",
            }}
          />
        </TodaysAttendanceChartContainer>
      </TodaysAttendanceContainer>
    );
  }

  return (
    <TodaysAttendanceContainer>
      <TodaysAttendanceHeader>
        <TodaysAttendanceTitle>Todays attendance</TodaysAttendanceTitle>
        <TodaysAttendanceIconButton>
          <img src={dashboardStatIconWhite} alt="Attendance icon" onClick={() => history.push(`/user/attendance`)} />
        </TodaysAttendanceIconButton>
      </TodaysAttendanceHeader>

      <TodaysAttendanceChartContainer>
        {/* Render concentric circles with attached legends */}
        {circles && circles.map(circle => {
          const isActive = activeBubble === circle.label;
          const activeZIndex = isActive ? 99 : circle.zIndex;
          const scale = isActive ? 1.08 : 1;

          // Calculate legend position relative to circle center
          const legendX = circle.x + circle.legendOffsetX;
          const legendY = circle.y + circle.legendOffsetY;

          return (
            <React.Fragment key={circle.label}>
              {/* Circle Background */}
              <AttendanceCircle
                style={{
                  width: `${circle.size}px`,
                  height: `${circle.size}px`,
                  backgroundColor: circle.color,
                  left: `${circle.x}px`,
                  top: `${circle.y}px`,
                  transform: `translate(-50%, -50%) scale(${scale})`,
                  transformOrigin: "center",
                  zIndex: activeZIndex,
                  transition:
                    "transform 0.2s ease, box-shadow 0.2s ease, z-index 0.2s ease",
                  cursor: "pointer",
                  boxShadow: isActive
                    ? "0 8px 24px rgba(141, 177, 247, 0.4)"
                    : "none",
                }}
                onMouseEnter={() => {
                  setActiveBubble(circle.label);
                }}
                onMouseLeave={() => setActiveBubble(null)}
              />

              {/* Text overlay for each circle - perfectly centered */}
              <BubbleText
                style={{
                  width: `${circle.size}px`,
                  height: `${circle.size}px`,
                  left: `${circle.x}px`,
                  top: `${circle.y}px`,
                  transform: "translate(-50%, -50%)",
                  zIndex: activeZIndex + 10,
                }}
              >
                {circle.value}
              </BubbleText>

              {/* Legend Item - attached to circle and moves with it */}
              <AttendanceLegendItem
                style={{
                  left: `${legendX}px`,
                  top: `${legendY}px`,
                  zIndex: activeZIndex + 5,
                  transition: "z-index 0.2s ease, background-color 0.2s ease, left 0.2s ease, top 0.2s ease",
                  backgroundColor: isActive
                    ? "rgba(255, 255, 255, 1)"
                    : "rgba(255, 255, 255, 0.8)",
                  boxShadow: isActive
                    ? "0 4px 12px rgba(0, 0, 0, 0.15)"
                    : "none",
                  position: "absolute",
                  transform: "none",
                }}
                onMouseEnter={() => {
                  setActiveBubble(circle.label);
                }}
                onMouseLeave={() => setActiveBubble(null)}
              >
                <AttendanceLegendDot color={circle.color} />
                <AttendanceLegendText>{circle.label}</AttendanceLegendText>
              </AttendanceLegendItem>
            </React.Fragment>
          );
        })}
      </TodaysAttendanceChartContainer>
    </TodaysAttendanceContainer>
  );
};

export default TodaysAttendance;
